import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
    useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { useStyles } from "./styles";
import { Box, TextField, Typography } from "@mui/material";
import MultiSelectSimple from "../../../../../../../components/dinamicForm/multiSelectSimple";
import { onChange } from "../../../../../../../utils/types";
import { Roles } from "../../../../../../../utils/enums";
import { CaptionKeyVertical } from "../../../../../../../components/detailKeyValue/keyCaption.index";
import { useTranslation } from "react-i18next";

interface PlaceAutocompleteProps {
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
    value: {
        name: string;
        direction: string;
        company?: {
            label: string;
            value: number;
        } | undefined;
    }
    setValue: Dispatch<SetStateAction<{
        name: string;
        direction: string;
        company?: {
            label: string;
            value: number;
        };
    }>>
    title?: string
    compnayId?: number
    role: number;
    companies: {
        id: number;
        value: number;
        label: string;
    }[];
    setError: React.Dispatch<React.SetStateAction<{
        name: boolean;
        direction: boolean;
        company: boolean;
    }>>;
    error: {
        name: boolean;
        direction: boolean;
        company: boolean;
    };
}

const PlaceAutocomplete = ({ onPlaceSelect, value, setValue, title, compnayId, role, companies, error, setError }: PlaceAutocompleteProps) => {
    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const inputRefConst = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary('places');
    const styles = useStyles()
    const { t } = useTranslation()


    useEffect(() => {
        if (!value || !inputRefConst.current) return;

        inputRefConst.current.value = value.direction; // Asignando valor correctamente
        //setInputValue(value);
    }, [value]);

    useEffect(() => {
        if (!places || !inputRefConst.current) return;

        const options = {
            fields: ['geometry', 'name', 'formatted_address'],
            strictBounds: false, // Evita que el autocompletado agregue su propio input
            //types: ['geocode']
        };

        setPlaceAutocomplete(new places.Autocomplete(inputRefConst.current, options));
    }, [places, inputRefConst.current]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        const listener = placeAutocomplete.addListener('place_changed', () => {
            //onPlaceSelect(placeAutocomplete.getPlace());
            const place = placeAutocomplete.getPlace();
            onPlaceSelect(place);
            setValue(prev => ({ ...prev, direction: place.formatted_address ?? '' }))
            setError(prev => ({ ...prev, direction: false }));
        });

        return () => {
            if (listener) {
                listener.remove();
            }
        };
    }, [onPlaceSelect, placeAutocomplete]);

    const handleInputChange: onChange = (event) => {
        const { value, name: nameInput } = event.target;

        setValue(prev => ({ ...prev, [nameInput]: value }));

        if (nameInput === 'name' || nameInput === 'direction') {
            setError(prev => ({
                ...prev,
                [nameInput]: value.length < 3
            }));
        }
    };


    return (
        <Box sx={styles.mapContainer}>
            {title && <Typography sx={styles.title}>{title}</Typography>}

            <Box sx={styles.container}>
                <Typography fontWeight={500}>{t('loogbook.titles.search')}</Typography>

                <TextField
                    inputRef={inputRefConst}
                    fullWidth
                    variant="outlined"
                    label="Dirección*"
                    name="direction"
                    placeholder="Ej: Calle 1 # 2-3, Barrio, Ciudad"
                    value={value.direction}
                    onChange={handleInputChange}
                    error={error.direction}
                    helperText={error.direction ? "El campo debe tener al menos 3 caracteres" : ""}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'grey.300', // Color del borde
                            },
                            '&:hover fieldset': {
                                borderColor: 'primary.main', // Color del borde al pasar el mouse
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main', // Color del borde al enfocar
                            },
                        },
                    }}
                />
            </Box>

            <Box sx={styles.container}>
                <Typography fontWeight={500}>{t('loogbook.titles.destinaitonInformation')}</Typography>

                <TextField
                    //inputRef={inputRefConst}
                    fullWidth
                    variant="outlined"
                    label="Nombre del destino*"
                    name="name"
                    //placeholder="Ej: Calle 1 # 2-3, Barrio, Ciudad"
                    value={value.name}
                    onChange={handleInputChange}
                    error={error.name}
                    helperText={error.name ? "El campo debe tener al menos 3 caracteres" : ""}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'grey.300', // Color del borde
                            },
                            '&:hover fieldset': {
                                borderColor: 'primary.main', // Color del borde al pasar el mouse
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main', // Color del borde al enfocar
                            },
                        },
                    }}
                />

                {!compnayId && role == Roles.ADMIN &&
                    <MultiSelectSimple
                        errors={error}
                        name='company'
                        label='Compañía*'
                        onChange={handleInputChange}
                        items={companies.map(item => ({
                            label: item.label,
                            value: item.id,
                            id: String(item.id)
                        }))}
                        selected={value.company}
                    />
                }

                {value.direction && <CaptionKeyVertical
                    caption={"Dirección :"}
                    value={value.direction ?? "N/A"}
                    captionProps={{ minWidth: '75px' }}
                />}
            </Box>

        </Box>
    );
};

export { PlaceAutocomplete }